import {useFilterListDetail} from '../../../../store/hooks/filterListDetail'
import {useConfig} from '../../../../store/hooks/config'
import {useUniqueAddressesCount} from '../../../../store/hooks/uniqueAddressesCount'
import {useFilterManagement} from '../../../../store/hooks/useFilterManagement'
import {getFilterConfig} from '../../../../utils/filterManagementHelpers'
import {useSelector} from 'react-redux'
import {selectCustomizations} from '../../../../store/slices/filterManagementSlice'

export function useCustomNameForDwellingType() {
  const {sdohFilter} = useFilterListDetail()
  return sdohFilter?.find((filter) => filter.title === 'Dwelling Type')?.FILTER_CUSTOM_NAME || 'Dwelling Type'
}

/**
 * Get selected Individual Health filters from new filter management system
 * @returns {Array} Array of objects with { key, name } for selected health conditions
 */
export function useSelectedIndividualHealthFilters() {
  const {getCategoryFilters, filterConfig} = useFilterManagement()
  const customizations = useSelector(selectCustomizations)

  // Individual Health category has id: "2" in newFilters.config.json
  const individualHealthFilters = getCategoryFilters('2')

  // Convert to array of {key, name} objects
  const selectedFilters = Object.keys(individualHealthFilters).map((filterKey) => {
    const config = getFilterConfig(filterKey, filterConfig)
    const customName = customizations?.filterNames?.[filterKey]
    const displayName = customName || config?.name || filterKey

    return {
      key: filterKey,
      name: displayName
    }
  })

  return selectedFilters
}
export function getTimeStampData(timeStampData) {
  return timeStampData?.timestamps?.[0]?.ORIGIN_TIMESTAMP
}

export function usePoiConfig() {
  const configData = useConfig()
  return configData?.POIConfig || {}
}

export function useUniqueAddressessCount() {
  const {uniqueAddressessCount} = useUniqueAddressesCount()
  return uniqueAddressessCount || 1000
}

export function convertPoiToGeoJSON(key, data) {
  const geoJSON = {
    type: 'FeatureCollection',
    features: []
  }

  // Convert hospitals to GeoJSON features
  if (key === 'hospitals') {
    for (const hospital of data) {
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [hospital.longitude, hospital.latitude]
        },
        properties: {
          type: key,
          name: hospital.name,
          address: hospital.address,
          city: hospital.city,
          state: hospital.state,
          zip: hospital.zip,
          beds: hospital.beds
        }
      }
      geoJSON.features.push(feature)
    }
  } else {
    for (const urgentcare of data) {
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [urgentcare.longitude, urgentcare.latitude]
        },
        properties: {
          type: key,
          name: urgentcare.name,
          address: urgentcare.address,
          city: urgentcare.city,
          state: urgentcare.state,
          zip: urgentcare.zip
        }
      }
      geoJSON.features.push(feature)
    }
  }

  return [key, geoJSON]
}

export function convertUniqueHouseholdsToGeoJSON(data) {
  const geoJSON = {
    type: 'FeatureCollection',
    features: []
  }

  // Convert hospitals to GeoJSON features

  for (const item of data) {
    const feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.longitude, item.latitude]
      },
      properties: {
        ...item
      }
    }
    geoJSON.features.push(feature)
  }

  return [geoJSON]
}

/**
 * List of functions and their purpose:
 *
 * - addDominantPersonaToTableData: Adds dominant persona information to population table data based on FIPS.
 * - checkOutreachSimulationFilter: Filters and processes selected outreach simulation options for table data.
 * - getBenefitsEligibilityCol: Retrieves column configurations for benefits eligibility based on selected filters.
 * - calculateCenter: Calculates the geographic center of given map bounds.
 * - handleCountyHover: Highlights a hovered county and updates the tooltip with relevant information.
 * - handleStateHover: Highlights a hovered state and updates the tooltip with relevant information.
 * - handleHover: Handles hover events for interactive layers and updates tooltips accordingly.
 * - handleLeave: Resets hover states and clears tooltips when the mouse leaves the map.
 * - renderClusterLayer: Renders cluster layers for points of interest based on their type.
 * - renderClusterCountLayer: Renders cluster count layers for points of interest based on their type.
 * - renderUnclusterLayer: Renders unclustered layers for points of interest based on their type.
 * - hasError: Checks if there are any errors in loading data or queries.
 * - checkForEmptyData: Checks if population aggregation or density data is empty.
 * - getTableDataToExport: Prepares table data for export based on selected filters and personas.
 * - getColumns: Configures table columns dynamically based on selected filters and personas.
 * - onload: Customizes map properties like text color and visibility on load.
 */
